import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import { Button } from "../components/ui/button";
import { Calendar, Video, Building2, Lock, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function MyAppointments() {
    const [items, setItems] = useState([]);
    const [revealing, setRevealing] = useState(null);
    const [revealed, setRevealed] = useState({});

    const load = async () => {
        try {
            const { data } = await api.get("/appointments/me");
            setItems(data || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load");
        }
    };
    useEffect(() => { load(); }, []);

    const reveal = async (a) => {
        setRevealing(a.id);
        try {
            const { data } = await api.get(`/appointments/${a.id}/doctor-contact`);
            setRevealed((cur) => ({ ...cur, [a.id]: data }));
        } catch (e) {
            if (e.response?.status === 402) {
                toast.error("Contact details unlock once your payment is confirmed.");
            } else {
                toast.error(e.response?.data?.detail || "Could not reveal contact");
            }
        } finally { setRevealing(null); }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-12" data-testid="my-appointments">
            <Toaster position="top-center" />
            <div className="label-eyebrow text-sky-700">Your bookings</div>
            <h1 className="font-serif text-4xl text-slate-900 mt-2 mb-8">My Appointments</h1>

            {items.length === 0 ? (
                <div className="border border-stone-200 bg-white p-12 text-center">
                    <Calendar className="w-8 h-8 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No appointments yet.</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {items.map((a) => {
                        const paid = a.payment_status === "paid";
                        const detail = revealed[a.id];
                        return (
                            <div key={a.id} className="bg-white border border-stone-200" data-testid={`appt-${a.id}`}>
                                <div className="p-4 grid grid-cols-12 gap-3 items-center">
                                    <div className="col-span-3">
                                        <div className="font-medium text-slate-900">{a.doctor_name}</div>
                                        <div className="text-xs text-stone-500 flex items-center gap-1 mt-1">
                                            {a.mode === "online" ? <Video className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                                            {a.mode === "online" ? "Online" : "Clinic visit"}
                                        </div>
                                    </div>
                                    <div className="col-span-3 text-sm">
                                        <div className="text-stone-700">{a.date}</div>
                                        <div className="text-xs text-stone-500">{a.time_slot}</div>
                                    </div>
                                    <div className="col-span-3 text-sm">
                                        <div className="text-stone-700">{formatINR(a.fee_total)}</div>
                                        <div className="text-xs">
                                            {paid
                                                ? <span className="text-emerald-700 inline-flex items-center"><CheckCircle2 className="w-3 h-3 mr-1" />Paid</span>
                                                : <span className="text-amber-700 inline-flex items-center"><Lock className="w-3 h-3 mr-1" />Payment pending</span>}
                                        </div>
                                    </div>
                                    <div className="col-span-3 text-right">
                                        {paid ? (
                                            <div className="flex flex-col gap-1.5 items-end">
                                                {a.mode === "online" && (
                                                    <Button asChild size="sm" className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid={`join-call-${a.id}`}>
                                                        <Link to={`/video-call/${a.id}`}><Video className="w-3.5 h-3.5 mr-1" />Join consultation</Link>
                                                    </Button>
                                                )}
                                                <Button
                                                    onClick={() => reveal(a)}
                                                    disabled={revealing === a.id || !!detail}
                                                    size="sm"
                                                    variant="outline"
                                                    className="rounded-none border-sky-700 text-sky-700 hover:bg-sky-50"
                                                    data-testid={`reveal-${a.id}`}
                                                >
                                                    {revealing === a.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Phone className="w-3.5 h-3.5 mr-1" />}
                                                    {detail ? "Showing contact" : "Show doctor's contact"}
                                                </Button>
                                            </div>
                                        ) : (
                                            <span className="text-[10px] uppercase tracking-widest text-stone-400">Pay to unlock contact</span>
                                        )}
                                    </div>
                                </div>
                                {detail && (
                                    <div className="bg-emerald-50 border-t border-emerald-200 px-4 py-3 grid sm:grid-cols-2 gap-3 text-sm">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-0.5">Phone</div>
                                            <div className="font-medium text-slate-900 flex items-center gap-1"><Phone className="w-3 h-3" />{detail.phone || "—"}</div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-0.5">Email</div>
                                            <div className="text-slate-900">{detail.email || "—"}</div>
                                        </div>
                                        {detail.clinic_name && (
                                            <div className="sm:col-span-2">
                                                <div className="text-[10px] uppercase tracking-widest text-emerald-700 mb-0.5">Clinic</div>
                                                <div className="text-slate-900 flex items-start gap-1"><MapPin className="w-3 h-3 mt-0.5 shrink-0" /><span>{detail.clinic_name}{detail.clinic_address ? `, ${detail.clinic_address}` : ""}{detail.city ? `, ${detail.city}` : ""}{detail.state ? `, ${detail.state}` : ""}</span></div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
